import { Plus, X } from "lucide-react";
import { useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { CHART_COLORS } from "./chartColors";
import { MAX_PEERS } from "./scoring-constants";
import type { Peer } from "./scoring-types";

interface PeerSelectionProps {
  selectedPeerIds: string[];
  peerPool: Peer[];
  peerColumns: (Peer | null)[];
  onAdd: (peerId: string) => void;
  onRemove: (peerId: string) => void;
}

export function PeerSelection({
  selectedPeerIds,
  peerPool,
  peerColumns,
  onAdd,
  onRemove,
}: PeerSelectionProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeIdx, setActiveIdx] = useState(-1);
  const listRef = useRef<HTMLDivElement>(null);

  // Use peerColumns order (same as chart) so chip colors match bar colors.
  const selectedPeers = peerColumns.filter(
    (p): p is Peer => p != null && selectedPeerIds.includes(p.id),
  );
  const availablePeers = peerPool.filter(
    (p) => !selectedPeerIds.includes(p.id),
  );
  const filteredPeers = search.trim()
    ? availablePeers.filter((p) =>
        p.name.toLowerCase().includes(search.toLowerCase()),
      )
    : availablePeers;

  const selectPeer = (peerId: string) => {
    onAdd(peerId);
    setPopoverOpen(false);
    setSearch("");
    setActiveIdx(-1);
  };

  const scrollItemIntoView = (idx: number) => {
    const list = listRef.current;
    if (!list) return;
    const item = list.children[idx] as HTMLElement | undefined;
    item?.scrollIntoView({ block: "nearest" });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (filteredPeers.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => {
        const next = Math.min(i + 1, filteredPeers.length - 1);
        scrollItemIntoView(next);
        return next;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => {
        const prev = Math.max(i - 1, 0);
        scrollItemIntoView(prev);
        return prev;
      });
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      selectPeer(filteredPeers[activeIdx].id);
    } else if (e.key === "Escape") {
      setPopoverOpen(false);
    }
  };

  const handlePopoverChange = (open: boolean) => {
    setPopoverOpen(open);
    if (!open) {
      setSearch("");
      setActiveIdx(-1);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setActiveIdx(-1);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {selectedPeers.map((peer, idx) => {
        // Color slot must match the chart: peerColumns index + 1 (slot 0 = account).
        const colIdx = peerColumns.findIndex((p) => p?.id === peer.id);
        const colorIdx = colIdx >= 0 ? colIdx + 1 : idx + 1;
        return (
          <Badge
            key={peer.id}
            variant="secondary"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium animate-chip-in"
            style={{ animationDelay: `${idx * 0.05}s` }}
          >
            <span
              className="size-2 rounded-full shrink-0"
              style={{
                background: CHART_COLORS[colorIdx % CHART_COLORS.length],
              }}
            />
            <span className="max-w-[120px] truncate">{peer.name}</span>
            <button
              type="button"
              onClick={() => onRemove(peer.id)}
              aria-label={`Remove ${peer.name}`}
              className="inline-flex items-center justify-center size-4 rounded-full text-muted-foreground hover:bg-destructive/15 hover:text-destructive transition-colors"
            >
              <X className="size-3" />
            </button>
          </Badge>
        );
      })}

      {selectedPeerIds.length < MAX_PEERS && availablePeers.length > 0 && (
        <Popover open={popoverOpen} onOpenChange={handlePopoverChange}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border border-dashed border-border text-muted-foreground hover:border-primary hover:text-primary hover:bg-primary/5 transition-colors cursor-pointer"
            >
              <Plus className="size-3.5" />
              Add Peer
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[240px] p-2" align="start">
            <div className="mb-2">
              <Input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                autoFocus
                className="h-8 text-sm"
              />
            </div>
            <div
              ref={listRef}
              className="space-y-1 max-h-[200px] overflow-y-auto"
            >
              {filteredPeers.length === 0 ? (
                <p className="text-xs text-muted-foreground px-2 py-2">
                  No results
                </p>
              ) : (
                filteredPeers.map((peer, idx) => (
                  <button
                    key={peer.id}
                    type="button"
                    onClick={() => selectPeer(peer.id)}
                    className={cn(
                      "w-full text-left text-sm px-2 py-1.5 rounded transition-colors truncate cursor-pointer",
                      idx === activeIdx
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-muted",
                    )}
                  >
                    {peer.name}
                  </button>
                ))
              )}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
